import {
  ESdChangeReason,
  SpecialDIIds,
  type IAction,
  type IComponent,
  type IActionDefinitionDto,
  type IActionParameter,
  type IDisplayItem,
  type IDisplayItemProperty,
  type IKTypeField,
  type IScreenDesignDictionary,
  type IEvent,
  type ISymbol
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import type { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { setDesignerActionParameter } from "../../designer-action-actions";
import type { ScreenDesignInstance } from "../../designer-context";
import { getValues } from "../../designer-context";
import {
  buildDesignerSymbol,
  enrichCurrentRowSymbol,
  setDesignerPropertySymbol,
  setDesignerPropertyValue,
  type IBuildDesignerSymbolInput
} from "../../designer-property-actions";
import {
  pickedValueListPropertiesOf,
  listDataHostFields,
  listRowFields,
  resolvePropertyWriteKind,
  rowFieldPath,
  setDesignerPropertyDataAction,
  setDesignerPropertyDataField,
  type PropertyWriteKind
} from "../../designer-property-data-editor-actions";
import { getDesignerAvailableEvents } from "../../designer-screen-query-actions";
import { ROOT_SECTIONS, type RootSectionDefinition } from "../../designer-style-actions";
import {
  closeDetachedScreenSession,
  openDetachedScreenSession,
  tryResolveEditTarget
} from "../../detached-screen-session";
import { KMainFunctions } from "../../main-functions";
import { findManagedScreenIdForStep, isEngineProcessAction } from "../../managed-task-screen";
import {
  findCatalogComponent,
  isPlacementRefusal,
  placeChildItem,
  placeComponent,
  removeComponent,
  reorderSibling,
  type IPlacementResult,
  type IRemovalResult,
  type IReorderResult
} from "../../screen-composition";
import { applyScreenComposition, type IApplyScreenCompositionResult } from "../../screen-composition-apply";
import {
  isLocallyInserted,
  recordConfigure,
  recordDelete,
  recordInsert,
  recordReorder,
  recordWire,
  type CompositionOperation
} from "../../screen-composition-ledger";
import {
  compositionDeclaredAlias,
  isScreenCompositionCommand,
  isScreenLoadEvent,
  orderByDeclaration,
  type CompositionAnchor,
  type CompositionSource,
  type ScreenCompositionCommand
} from "./workflow-dsl-parser";

export { isScreenCompositionCommand };
export type { ScreenCompositionCommand };

/**
 * KOA-18431 — COMPOSING a user task's managed screen, from the script that builds the process.
 *
 * ## Why this is a pass and not eight cases in the command loop
 *
 * The screen does not exist when the script starts. `ASSIGN_SCREEN` is what generates it, server
 * side, and the ids these commands address only come into being then. So composition runs AFTER
 * the main loop, per step, in one open-compose-send cycle — the same cycle the removed screen
 * builder performed by hand, in the same order, through the same functions.
 *
 * ## The send is per STEP, and a refusal there is total
 *
 * `applyScreenComposition` sends one step's operations inside the apply that regenerates its form,
 * and the server's applier STOPS AT THE FIRST REFUSAL and fails the whole step. A line that the
 * server would refuse must therefore never reach the wire: every guard the browser has —
 * placeability, the catalog, the platform-owned regions — runs here first, and a refused line
 * fails ALONE while its neighbours still travel.
 *
 * ## Nothing here re-implements the capability
 *
 * Placement, child items, reorder and removal are the screen-agnostic operations; properties and
 * symbols go through the designer's own writers with the screen named; an action is built exactly
 * as the platform's action picker builds it and attached through the session's own
 * `saveActionToEvent`. This module is the consumer that was missing, not a second implementation.
 */

/** One composition line's outcome, in the shape the executor reports every other line. */
export interface IScreenCompositionLineResult {
  line: number;
  command: string;
  success: boolean;
  message: string;
}

/** The composition commands, in the order the author wrote them. */
/** How the caller turns a step ref (`@alias` or a literal id) into the real BPMN step id. */
export type StepRefResolver = (ref: string) => string;

/**
 * The platform's own name for a literal key/text option list, as the data editor stores it.
 *
 * Spelled here rather than in the grammar so the author writes `fixed` and the platform's exact
 * string stays one constant next to the call that sends it.
 */
const FIXED_OPTION_LIST: string = "Fixed Option List";

/** An action instance this script attached, and the component it hangs off. */
interface IAttachedAction {
  actionId: string;
  hostKuikaId: number;
}

/** One step's screen, and everything this script has named on it. */
interface IStepWorkspace {
  stepId: string;
  screenId: string;
  /** True when the screen was pulled into a session here and must be dropped afterwards. */
  ownsSession: boolean;
  components: Map<string, number>;
  actions: Map<string, IAttachedAction>;
  operations: CompositionOperation[];
  /**
   * KOA-18431 — the lists this script gave a source to, so their columns can be completed from
   * that source once every line has run. Keyed by component id: two lines naming the same host
   * are one list.
   */
  dataHosts: Map<number, IDataHostBinding>;
  /**
   * KOA-18431 — every component this step's LINES created, by id.
   *
   * A sweep must never touch something that was already on the screen, and "this script placed
   * it" is not answerable from the tree afterwards. Keyed by id and carrying the catalog name, so
   * the sweep can ask what a thing IS without re-reading the tree it is about to change.
   */
  createdComponents: Map<number, string>;
  /**
   * Every alias this step's lines bring into being.
   *
   * Held so a refusal can tell the two cases apart. Before KOA-18431 every unresolved alias was
   * reported as "nothing in this script placed it", which on a forward reference was simply false
   * — the line that placed it was four lines down — and sent the reader looking for a typo.
   */
  declaredAliases: ReadonlySet<string>;
}

/**
 * Record a placement — unless the thing it went INTO is also this script's.
 *
 * An insert carries the placed component's WHOLE subtree, so a component placed inside another
 * component this script placed already travels inside that one's operation. Recording it again
 * would send the same subtree twice; the server recognises the duplicate and skips it ("create
 * once"), so it is not a correctness problem — it is a payload that says the same thing twice, on
 * a request that already carries a form's worth of metadata.
 */
/**
 * Whether this component is INSIDE something this script already staged for insert.
 *
 * The ledger answers the same question about an id it holds, and that used to be enough: every
 * anchor a composition line touched was a component the script had aliased, so "the anchor is a
 * staged insert" and "the anchor is inside one" were the same sentence. They are not any more.
 * `PLACE` lands a whole SUBTREE — a Table arrives with its bands, its columns and their Labels —
 * and both `FIND` and the catalog's own child redirect hand back ids from INSIDE it. Those ids are
 * not in the ledger and never will be; the server receives them as part of the table.
 *
 * Asking only about the id itself therefore stages work the server must not do: an insert of a
 * column into a row it is about to be given with that column already in it, a configure of a
 * property the subtree carries. So the walk is upward, and it lives here rather than in the ledger
 * because the ledger is pure and holds no tree — the tree is this module's to read.
 */
function withinStagedInsert(
  operations: readonly CompositionOperation[],
  dictionary: IScreenDesignDictionary | undefined,
  kuikaId: number | undefined
): boolean {
  let current: number | undefined = kuikaId;
  // Bounded: a corrupt parent chain must not spin. Far beyond any real screen.
  for (let hops: number = 0; current !== undefined && hops < 256; hops++) {
    if (isLocallyInserted(operations, current)) return true;
    current = dictionary?.[current]?.parentId ?? undefined;
  }
  return false;
}

function stageInsert(
  operations: readonly CompositionOperation[],
  placed: IPlacementResult,
  dictionary: IScreenDesignDictionary | undefined
): CompositionOperation[] {
  // KOA-18431 — what the SCREEN gained, not what the author named. A placement on the screen body
  // builds the grid the platform puts a control in (`Row → Col → control`), and the server has
  // never seen that Col: staging the control would ask it to create one inside a component that
  // does not exist there. The insert root is the Row in that case and the component itself in
  // every other, and its subtree carries the rest.
  if (withinStagedInsert(operations, dictionary, placed.insertRootAnchorKuikaId)) return [...operations];
  return recordInsert(operations, {
    kuikaId: placed.insertRootKuikaId,
    anchorKuikaId: placed.insertRootAnchorKuikaId,
    componentName: placed.componentName
  });
}

/** A property change, unless the component travels inside a subtree this script staged. */
function stageConfigure(
  operations: readonly CompositionOperation[],
  dictionary: IScreenDesignDictionary | undefined,
  kuikaId: number,
  propertyName: string
): CompositionOperation[] {
  if (withinStagedInsert(operations, dictionary, kuikaId)) return [...operations];
  return recordConfigure(operations, { kuikaId, propertyName });
}

/** An event-wiring change, under the same rule. */
function stageWire(
  operations: readonly CompositionOperation[],
  dictionary: IScreenDesignDictionary | undefined,
  kuikaId: number
): CompositionOperation[] {
  if (withinStagedInsert(operations, dictionary, kuikaId)) return [...operations];
  return recordWire(operations, kuikaId);
}

/** A child-order change, under the same rule. */
function stageReorder(
  operations: readonly CompositionOperation[],
  dictionary: IScreenDesignDictionary | undefined,
  kuikaId: number
): CompositionOperation[] {
  if (withinStagedInsert(operations, dictionary, kuikaId)) return [...operations];
  return recordReorder(operations, { kuikaId });
}

const ok = (command: ScreenCompositionCommand, message: string): IScreenCompositionLineResult => ({
  line: command.line,
  command: command.kind,
  success: true,
  message
});

const fail = (command: ScreenCompositionCommand, message: string): IScreenCompositionLineResult => ({
  line: command.line,
  command: command.kind,
  success: false,
  message
});

/**
 * The step a line belongs to.
 *
 * Only `PLACE` names one; every other line inherits it from the alias it addresses, because a
 * component belongs to exactly one screen. A line addressing an alias nothing placed has no step,
 * and that is a refusal rather than a guess — the alternative is composing onto whichever screen
 * happened to be first.
 */
function stepOfCommand(
  command: ScreenCompositionCommand,
  stepByAlias: ReadonlyMap<string, string>
): string | undefined {
  if (command.kind === "PLACE") return command.stepId;
  if (command.kind === "CHILD") return stepByAlias.get(command.hostAlias);
  if (command.kind === "FIND") {
    return command.host.kind === "step" ? command.host.stepRef : stepByAlias.get(command.host.alias);
  }
  if (command.kind === "ACTION") {
    return command.host.kind === "step" ? command.host.stepRef : stepByAlias.get(command.host.alias);
  }
  if (command.kind === "ARG") return stepByAlias.get(command.actionAlias);
  if (command.kind === "DROP") {
    return command.target.kind === "alias" ? stepByAlias.get(command.target.alias) : undefined;
  }
  return stepByAlias.get(command.alias);
}

/**
 * alias → step, for every alias this script creates.
 *
 * Built over the WHOLE script before anything runs, so a line may address an alias declared below
 * it. The executor's own edge-deferral makes the same allowance for BPMN refs, and an author
 * writing a screen top-down produces exactly this shape.
 */
function mapAliasesToSteps(commands: readonly ScreenCompositionCommand[]): Map<string, string> {
  const stepByAlias: Map<string, string> = new Map<string, string>();
  // PLACE roots the mapping; CHILD, FIND and ACTION inherit from the alias they hang off, so a repeated
  // sweep settles chains of any depth (a column of a table of a panel) without recursion.
  for (const command of commands) {
    if (command.kind === "PLACE") stepByAlias.set(command.alias, command.stepId);
  }
  for (let pass = 0; pass < 8; pass++) {
    let grew: boolean = false;
    for (const command of commands) {
      if (command.kind === "CHILD") {
        const host: string | undefined = stepByAlias.get(command.hostAlias);
        if (host !== undefined && !stepByAlias.has(command.alias)) {
          stepByAlias.set(command.alias, host);
          grew = true;
        }
      }
      if (command.kind === "FIND") {
        const host: string | undefined =
          command.host.kind === "step" ? command.host.stepRef : stepByAlias.get(command.host.alias);
        if (host !== undefined && !stepByAlias.has(command.alias)) {
          stepByAlias.set(command.alias, host);
          grew = true;
        }
      }
      if (command.kind === "ACTION" && command.actionAlias !== undefined) {
        const host: string | undefined =
          command.host.kind === "step" ? command.host.stepRef : stepByAlias.get(command.host.alias);
        if (host !== undefined && !stepByAlias.has(command.actionAlias)) {
          stepByAlias.set(command.actionAlias, host);
          grew = true;
        }
      }
    }
    if (!grew) break;
  }
  return stepByAlias;
}

/**
 * Why an alias did not resolve, in the author's words.
 *
 * Declared-but-absent and never-declared are different mistakes and read differently: the first
 * means an earlier line of this script failed (or could not be satisfied at all), the second means
 * the name is not in the script and is usually a typo.
 */
function unresolvedAlias(alias: string, workspace: IStepWorkspace, kind: "component" | "action"): string {
  const noun: string = kind === "action" ? "an action" : "a component";
  if (workspace.declaredAliases.has(alias)) {
    return (
      `${alias} is declared in this script but never landed on the screen — the line that creates ` +
      `it did not succeed, so this line has no ${kind} to address.`
    );
  }
  return `${alias} is not ${noun} on this screen — no line in this script names it.`;
}

/**
 * The events a host DECLARES, or `undefined` when the screen cannot say.
 *
 * Delegates to `getDesignerAvailableEvents` — the platform's OWN reader, the one `get_events`,
 * `get_info` and the property panel answer from — rather than asking the catalog by component
 * name, which is what this did first. Two reasons the second copy had to go. It read a different
 * SOURCE: the catalog holds the project-wide definition, while `di.item.properties` is the
 * instance's own copy and is what the panel renders, so a component whose instance and catalog had
 * drifted would be gated on events the panel does not show. And it matched by NAME, while every
 * other definition lookup in the platform matches by `item.id` (`getComponentDefinition` →
 * `findComponentById`), so a renamed catalog entry would have silently disagreed.
 *
 * `undefined` means NO EVIDENCE and the caller fails open: a gate that refuses on missing evidence
 * blocks correct lines. An empty ARRAY is an answer — this host declares no events — and refuses.
 * The two are told apart here because the platform's reader returns `[]` for both.
 */
function declaredEventNames(target: IScreenDesignContextState, kuikaId: number): string[] | undefined {
  const key: string = String(kuikaId);
  // The screen's own sections are not catalog components at all; `ROOT_SECTIONS` states theirs,
  // and the body's is the `pageInit` this whole check exists for. Read straight from the same map
  // the platform's reader consults, so no display item has to be invented to ask about a section.
  const rootSection: RootSectionDefinition | undefined = ROOT_SECTIONS[key];
  if (rootSection !== undefined) return [...rootSection.availableEvents];

  const di: IDisplayItem | undefined = target.screenDesignDictionary?.[kuikaId];
  if (di === undefined || !Array.isArray(di.item?.properties)) return undefined;
  return getDesignerAvailableEvents(di, key);
}

/**
 * Everything inside a host, in the order the screen reads.
 *
 * `FIND` searches the SUBTREE rather than the direct children, because the author cannot see the
 * screen and the nesting is not theirs. A Table's columns hang off its row; a component placed
 * `IN screen` may sit inside the Col an earlier script put it in. Making the author state the path
 * would be the same trap as making them know that columns are the row's — they would have to guess,
 * and a guess that misses is a refused line rather than a wrong screen only by luck.
 *
 * Document order, so `n` counts the way the screen is read, and depth-bounded so a corrupt tree
 * cannot spin.
 */
function descendantsOf(host: IDisplayItem | undefined, depth: number = 0): IDisplayItem[] {
  if (host === undefined || depth > 64) return [];
  const found: IDisplayItem[] = [];
  for (const child of host.children ?? []) {
    found.push(child);
    found.push(...descendantsOf(child, depth + 1));
  }
  return found;
}

/** The component id an anchor names, or a refusal in the author's words. */
function resolveAnchor(anchor: CompositionAnchor, workspace: IStepWorkspace): number | string {
  if (anchor.kind === "root" || anchor.kind === "kuikaId") return anchor.kuikaId;
  const resolved: number | undefined = workspace.components.get(anchor.alias);
  if (resolved === undefined) {
    return unresolvedAlias(anchor.alias, workspace, "component");
  }
  return resolved;
}

/**
 * Turn a DSL source into the platform's own binding input.
 *
 * Every arm names a `bindingType` the symbol factory already implements, so the symbol a script
 * produces is the symbol the property picker would have produced by hand. `row:` is the one
 * translation: the platform spells a row-context binding as the default value `current.<Field>`,
 * and the factory promotes it to a row symbol.
 */
function bindingInputFor(
  source: CompositionSource,
  workspace: IStepWorkspace,
  screenId: string
): IBuildDesignerSymbolInput | string {
  const base = { screenId };
  if (source.kind === "fixed") return { ...base, bindingType: "fixed", fixedValue: source.value };
  if (source.kind === "processParameter") return { ...base, bindingType: "processParameter", inputName: source.name };
  if (source.kind === "screenInput") return { ...base, bindingType: "screenInput", inputName: source.name };
  if (source.kind === "default") return { ...base, bindingType: "default", defaultName: source.name };
  if (source.kind === "currentRow")
    return { ...base, bindingType: "default", defaultName: `current.${source.fieldName}` };

  if (source.kind === "componentValue") {
    const sourceKuikaId: number | undefined = workspace.components.get(source.alias);
    if (sourceKuikaId === undefined) {
      return unresolvedAlias(source.alias, workspace, "component");
    }
    return {
      ...base,
      bindingType: "componentValue",
      sourceKuikaId: String(sourceKuikaId),
      sourcePropertyName: source.propertyName
    };
  }

  // Both remaining forms — the whole result and one of its fields — name the action the same way.
  const attached: IAttachedAction | undefined = workspace.actions.get(source.actionAlias);
  if (attached === undefined) {
    return unresolvedAlias(source.actionAlias, workspace, "action");
  }
  if (source.kind === "actionResult") {
    return { ...base, bindingType: "actionResult", sourceActionId: attached.actionId };
  }
  return { ...base, bindingType: "actionField", sourceActionId: attached.actionId, fieldName: source.fieldName };
}

/**
 * WRITE one bound property through the writer the PLATFORM gives that property — KOA-18431.
 *
 * ## The defect this closes
 *
 * Every composition line used to end at `setDesignerPropertySymbol`, the ordinary symbol writer.
 * That is the right writer for an ordinary property and the WRONG one for the two kinds this
 * surface reaches for most:
 *
 *   - a table's `dataSource` and a dropdown's `options` are owned by the DATASOURCE picker, which
 *     records the action it bound (`actionDefinitionObjectPath`, `resultActionId`) and clears the
 *     column names a previous source left behind;
 *   - a cell's `value` inside a table is owned by the COLUMN FIELD picker, which writes the row
 *     token `[datafield:…]`, the field's own type and its definition, and rebuilds the column's
 *     edit-mode component for that type.
 *
 * Written through the symbol writer instead, both come out as a symbol that LOOKS bound: the call
 * succeeds, the property panel's own editors read fields that are not there, and code generation
 * loses the field reference. Live `c74f551e` is the whole cost — three columns with correct
 * headers, every cell empty, and every line reported green.
 *
 * KOA-17797 fixed exactly this on the AI's ordinary-screen path, and left the reason in
 * `enrichTableFieldBindingSymbol`'s own words. This routes instead of enriching: the platform's
 * writer is called, so there is nothing to reconcile afterwards.
 */
interface IWriteRefusal {
  error: string;
}

interface IWriteSuccess {
  message: string;
}

function isWriteRefusal(result: IWriteSuccess | IWriteRefusal): result is IWriteRefusal {
  return (result as IWriteRefusal).error !== undefined;
}

interface IBoundPropertyWrite {
  sdInstance: ScreenDesignInstance;
  scoped: IScreenDesignContextState;
  workspace: IStepWorkspace;
  screenId: string;
  kuikaId: number;
  alias: string;
  propertyName: string;
  source: CompositionSource;
}

async function writeBoundProperty(input: IBoundPropertyWrite): Promise<IWriteSuccess | IWriteRefusal> {
  const { sdInstance, scoped, workspace, screenId, kuikaId, alias, propertyName, source } = input;
  const target = { screenId, kuikaId: String(kuikaId), propertyName };
  const writeKind: PropertyWriteKind | IWriteRefusal = resolvePropertyWriteKind(sdInstance, target);
  if (typeof writeKind !== "string") return writeKind;

  if (writeKind === "data-action") {
    // A datasource-picker property takes an ACTION, not a symbol. The other source forms have no
    // meaning here and would be written as a value the picker cannot read back, so they are
    // refused at the line rather than stored.
    if (source.kind !== "actionResult") {
      return {
        error:
          `"${propertyName}" of ${alias} is a DATA property: it reads from an action, and the ` +
          `platform binds it through its own datasource picker. Name the action — ` +
          `\`BIND ${alias} ${propertyName} action:#<actionAlias>\` for one this script attached, or ` +
          `\`DATA ${alias} ${propertyName} "<ActionName>"\` — instead of a ${source.kind} source.`
      };
    }
    const attached: IAttachedAction | undefined = workspace.actions.get(source.actionAlias);
    if (attached === undefined) return { error: unresolvedAlias(source.actionAlias, workspace, "action") };
    const found = findAttachedAction(scoped, attached);
    if (found === undefined) return { error: `${source.actionAlias} is no longer on this screen.` };
    // The picker selects by the action INSTANCE's label, tag included — the same string its own
    // option list carries, so an action attached twice stays distinguishable.
    const label: string = found.action.tag ? `${found.action.label}#${found.action.tag}` : found.action.label;
    const written = setDesignerPropertyDataAction(sdInstance, { ...target, value: label });
    if ("error" in written && written.error !== undefined) return { error: written.error };
    workspace.dataHosts.set(kuikaId, {
      kuikaId,
      propertyName,
      filterFields: parameterNamesOf(found.action.parameters)
    });
    return { message: `${propertyName} of ${alias} reads from ${label}.` };
  }

  if (writeKind === "row-field") {
    // A cell inside a data repeater. `row:` is the field picker; anything else is the platform's
    // own "Symbol Picker" mode, which has to be declared before the symbol is written or the
    // panel keeps reading the value as a row field.
    if (source.kind === "currentRow") {
      const available: IKTypeField[] | IWriteRefusal = listRowFields(sdInstance, target);
      if (!Array.isArray(available)) return available;
      const wanted: string = source.fieldName.trim().toLowerCase();
      const match: IKTypeField | undefined = available.find(
        (field: IKTypeField): boolean => rowFieldPath(field).toLowerCase() === wanted
      );
      if (match === undefined) {
        const offered: string = available.map(rowFieldPath).join(", ");
        return {
          error:
            `row:${source.fieldName} — the list this cell sits in does not carry a field by that ` +
            `name. It offers: ${offered.length > 0 ? offered : "(none)"}. Bind the ` +
            `list's datasource first, then name one of its own columns.`
        };
      }
      // The FIELD, serialized — the same operand the platform's own picker submits. Its definition
      // travels into the symbol, which is what the property panel reads back and what code
      // generation resolves the binding through; a bare name resolves to the same field and leaves
      // that definition behind.
      const written = await setDesignerPropertyDataField(sdInstance, {
        ...target,
        key: "tableFieldBinding",
        value: JSON.stringify(match)
      });
      if (written?.error !== undefined) return { error: written.error };
      return { message: `${propertyName} of ${alias} shows the row's ${rowFieldPath(match)}.` };
    }
    const declared = await setDesignerPropertyDataField(sdInstance, {
      ...target,
      key: "tableLabelSourceType",
      value: "symbolPicker",
      resetSymbol: false
    });
    if (declared?.error !== undefined) return { error: declared.error };
  }

  const buildInput: IBuildDesignerSymbolInput | string = bindingInputFor(source, workspace, screenId);
  if (typeof buildInput === "string") return { error: buildInput };
  const symbol: ISymbol | { error: string } = buildDesignerSymbol(buildInput, sdInstance);
  if ("error" in symbol) return { error: symbol.error };
  const written = setDesignerPropertySymbol(sdInstance, { ...target, symbol: symbol as ISymbol });
  if ("error" in written && written.error !== undefined) return { error: written.error };
  return { message: `${propertyName} of ${alias} bound.` };
}

/**
 * KOA-18431 — WHAT A LIST SHOWS IS THE READ'S OWN FIELDS, NOT WHAT THE AUTHOR REMEMBERED.
 *
 * Live `hyfzhp`: the builder placed the table, attached the read, bound the datasource and wrote
 * exactly ONE column — "Tutar". That read returns FIFTEEN fields. The screen shipped a one-column
 * list of the requester's earlier requests, with every line green, because nothing anywhere says
 * how many columns a list needs: the only rule on the subject refuses a list with NO cells, and one
 * cell satisfies it.
 *
 * The information was never missing. The catalog reports every field a read returns, the brief
 * carries it, and the platform's own column picker is a select list over exactly those. It was
 * simply never connected to the columns — the builder's teaching uses `returnFields` for a
 * different question entirely (which later STEP can consume a value), so a list's columns were left
 * to be remembered.
 *
 * So they are completed from the read itself: what the author wrote stays, in their order and with
 * their titles, and every remaining field the list could show gets a column. Deterministic, which
 * is what makes it hold on the on-prem floor — the prompt teaches the same thing so a well-behaved
 * run never reaches here, and this is what a run that stops early ships anyway.
 */
interface IDataHostBinding {
  /** The component holding the list. */
  kuikaId: number;
  /** The property its source was bound to (`dataSource`, or a widget's own). */
  propertyName: string;
  /**
   * The INPUT names of the read behind it — KOA-18431.
   *
   * A field the read is filtered BY holds the same value in every row it returns, so a column of
   * it is a column of one repeated value. Live `5609cb0d` shipped exactly that: a list of the
   * requests in the chosen category, with "Expense Category" as a column. The author may still
   * write that column — this only keeps the completion pass from adding one nobody asked for.
   */
  filterFields: readonly string[];
}

/** The input names of an action instance, as the completion pass compares them. */
function parameterNamesOf(parameters: readonly IActionParameter[] | undefined): string[] {
  return (parameters ?? [])
    .map((parameter: IActionParameter): string => (parameter.name ?? "").trim())
    .filter((name: string): boolean => name.length > 0);
}

/**
 * Fields a list must not put in a column of its own.
 *
 * Identity and plumbing: a primary key, a foreign key, the process instance the row belongs to,
 * the soft-delete marker, the audit user columns. They are how rows are FOUND, never what a person
 * reads off one. Timestamps are deliberately NOT here — `CreatedAt` is the date a person expects to
 * see on a list of past requests, and dropping it is the same silence this exists to close.
 *
 * ## The identifier is a WORD, and where the name cannot say so, the type can
 *
 * This asked the question of the FOLDED name — `endsWith("id")` — which also drops `Paid`,
 * `Valid`, `Solid`, `Void`, `Grid` and `Unpaid`: columns a person reads off a row, dropped in
 * silence, which is exactly what this pass exists to end. The reference is therefore matched on
 * its own word boundary — the capital of the PascalCase form (`ProcessInstanceId`) or the
 * separator of the snake one (`<Entity>_Id`), which is also how the grammar's own teaching spells
 * it.
 *
 * That leaves the key a lowercase schema spells with no boundary at all (`expenseid`), which no
 * name test can answer for. Its TYPE does: every key the platform mints is a `Guid`, and a Guid
 * on a list is a hex string nobody reads. So the type answers where the name cannot, and a
 * lowercase key stays out of the columns without a `Paid` going with it.
 */
function isDisplayableRowField(field: IKTypeField): boolean {
  if (field.isList === true) return false;
  const name: string = (field.name ?? "").trim();
  if (name.length === 0) return false;
  const folded: string = name.toLowerCase();
  if (folded === "id" || folded === "isdeleted") return false;
  if (folded === "createduser_id" || folded === "updateduser_id") return false;
  if (name.endsWith("Id") || folded.endsWith("_id")) return false;
  if ((field.type?.name ?? "").trim().toLowerCase() === "guid") return false;
  return true;
}

/**
 * A field name as a parameter name and a column name can be COMPARED — KOA-18431.
 *
 * The same concept is spelled `ExpenseCategory` in a result and `expense_category` in a parameter
 * often enough that a case fold alone misses the match, and missing it is what puts a column of
 * one repeated value on a filtered list. Case and the separators only; nothing is stemmed, so
 * `Category` and `Categories` stay two different names.
 */
function foldFieldName(name: string): string {
  return name
    .replace(/[\s_-]/g, "")
    .trim()
    .toLowerCase();
}

/** A human column header from a field name, the way the platform labels a generated form field. */
function humanizeFieldName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
}

/** Every row field ALREADY shown somewhere inside a host, by the binding the platform writes. */
function boundRowFields(target: IScreenDesignContextState, hostKuikaId: number): Set<string> {
  const shown: Set<string> = new Set<string>();
  const host: IDisplayItem | undefined = target.screenDesignDictionary?.[hostKuikaId];
  for (const descendant of [host, ...descendantsOf(host)]) {
    for (const property of descendant?.item?.properties ?? []) {
      const fieldName: unknown = property?.symbol?.fieldName;
      if (typeof fieldName === "string" && fieldName.trim().length > 0) {
        shown.add(fieldName.trim().toLowerCase());
      }
    }
  }
  return shown;
}

/**
 * One entry of a fixed option list, in the platform's own spelling.
 *
 * `key` is what the control submits and `text` is what the person reads — the shape the manual
 * key/text editor writes and the shape `fieldToUseAsKey` / `fieldToDisplay` name once the writer
 * has marked the symbol fixed.
 */
interface IFixedOption {
  key: string;
  text: string;
}

/**
 * The fixed options a property already carries.
 *
 * Stored as a JSON STRING by the platform (the manual editor stringifies on write and parses on
 * read), so anything else found there — an action-bound list, a half-written value — reads as no
 * options rather than throwing: the caller is about to write the list, and a refusal here would
 * make a control that once read from an action impossible to convert to a literal list.
 */
function readFixedOptions(target: IScreenDesignContextState, kuikaId: number, propertyName: string): IFixedOption[] {
  const di: IDisplayItem | undefined = target.screenDesignDictionary?.[kuikaId];
  const property = (di?.item?.properties ?? []).find(
    (candidate: IDisplayItemProperty): boolean => candidate.name === propertyName
  );
  const raw: unknown = property?.symbol?.fixedValue;
  const parsed: unknown = typeof raw === "string" ? safeParseJson(raw) : raw;
  if (!Array.isArray(parsed)) return [];
  const options: IFixedOption[] = [];
  for (const entry of parsed) {
    if (entry === null || typeof entry !== "object") continue;
    const candidate = entry as { key?: unknown; text?: unknown };
    if (typeof candidate.key !== "string") continue;
    options.push({ key: candidate.key, text: typeof candidate.text === "string" ? candidate.text : candidate.key });
  }
  return options;
}

function safeParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

/** Whether a property currently holds a LITERAL option list rather than an action-fed one. */
function isFixedOptionList(target: IScreenDesignContextState, kuikaId: number, propertyName: string): boolean {
  const di: IDisplayItem | undefined = target.screenDesignDictionary?.[kuikaId];
  const property = (di?.item?.properties ?? []).find(
    (candidate: IDisplayItemProperty): boolean => candidate.name === propertyName
  );
  return property?.symbol?.isFixed === true;
}

/**
 * The two data-editor keys that name COLUMNS OF AN ACTION'S RESULT.
 *
 * On a fixed list they are the platform's own `key` / `text`, set by the writer that marks the
 * symbol fixed; naming anything else there points the control at members its entries do not have.
 */
const OPTION_COLUMN_KEYS: ReadonlySet<string> = new Set<string>(["fieldtodisplay", "fieldtouseaskey"]);

function isOptionColumnKey(key: string): boolean {
  return OPTION_COLUMN_KEYS.has(key.trim().toLowerCase());
}

/** The action definition this project offers under `name`, matched the way every other lookup does. */
function findActionDefinition(target: IScreenDesignContextState, name: string): IActionDefinitionDto | undefined {
  const definitions: IActionDefinitionDto[] = target.actionDefinitions ?? [];
  const wanted: string = name.trim().toLowerCase();
  return definitions.find(
    (candidate: IActionDefinitionDto): boolean =>
      (candidate.name ?? "").trim().toLowerCase() === wanted || candidate.id === name
  );
}

/**
 * Build an action instance from its definition — the platform's own construction, copied from the
 * action picker (`ActionPicker.onActionSelected`) rather than invented: a fresh instance id, the
 * definition's name as both label and description, its parameters cloned, and its return type
 * carried so a later binding to the result knows what it is.
 */
function buildActionInstance(definition: IActionDefinitionDto): IAction {
  const parameters: IActionParameter[] = (definition.actionParameters ?? []).map(
    (parameter: IActionParameter): IActionParameter => {
      const clone: IActionParameter = { ...parameter };
      if (clone.symbol === undefined) clone.symbol = KMainFunctions.getDefaultSymbol(clone.Typename);
      return clone;
    }
  );
  return {
    id: Guid.create().toString(),
    actionDefinitionId: definition.id,
    label: definition.name,
    description: definition.name,
    parameters,
    returnType: definition.returnTypeName,
    returnCardinality: definition.returnCardinality
  } as unknown as IAction;
}

/** The action instance `actionId` on `hostKuikaId`, and the event it sits on. */
function findAttachedAction(
  target: IScreenDesignContextState,
  attached: IAttachedAction
): { action: IAction; event: IEvent } | undefined {
  const di: IDisplayItem | undefined = target.screenDesignDictionary?.[attached.hostKuikaId];
  for (const event of di?.item?.events ?? []) {
    const action: IAction | undefined = (event.actions ?? []).find(
      (candidate: IAction): boolean => candidate.id === attached.actionId
    );
    if (action !== undefined) return { action, event };
  }
  return undefined;
}

/**
 * The properties this step gives a source to, per alias — KOA-18431.
 *
 * Read from the step's WHOLE command list before any line runs, because "line order does not
 * matter" is the grammar's promise: the list a control shows may be written above or below the
 * line that places it. Only a line that actually supplies the list counts — `DATA` names its
 * action or declares it literal, `BIND` points it at one this script attached, `OPTION` writes an
 * entry. `DATAFIELD` names COLUMNS of a source and is not one.
 */
function sourcedPropertiesByAlias(commands: readonly ScreenCompositionCommand[]): Map<string, Set<string>> {
  const sourced: Map<string, Set<string>> = new Map<string, Set<string>>();
  const record = (alias: string, propertyName: string): void => {
    const key: string = alias.trim();
    const existing: Set<string> | undefined = sourced.get(key);
    const name: string = propertyName.trim().toLowerCase();
    if (existing === undefined) sourced.set(key, new Set<string>([name]));
    else existing.add(name);
  };
  for (const command of commands) {
    if (command.kind === "DATA" || command.kind === "BIND" || command.kind === "OPTION") {
      record(command.alias, command.propertyName);
    }
  }
  return sourced;
}

/**
 * The aliases something on this step USES — KOA-18431.
 *
 * A control exists to be used, and a script says so in one of three ways:
 *
 *   - an action hangs off it — `ACTION #ctrl <event> …`;
 *   - something takes its value — any source spelled `component:#ctrl.<property>`, whether that is
 *     a read's argument (`ARG`) or another component's property (`BIND`);
 *   - the running process carries its value — `BIND #ctrl <property> process:<name>`, which is how
 *     a task screen's own form fields are filled and written back.
 *
 * A control in none of the three is furniture: live `5609cb0d` shipped a category filter with five
 * choices that nothing listened to, because the single line that read it was refused and the
 * control stayed. The third clause is what keeps this rule off a data-entry field — refusing one of
 * those would be a worse failure than the one it is here to catch.
 */
function consumedAliases(commands: readonly ScreenCompositionCommand[]): Set<string> {
  const consumed: Set<string> = new Set<string>();
  for (const command of commands) {
    if (command.kind === "ACTION" && command.host.kind === "alias") consumed.add(command.host.alias.trim());
    if (command.kind === "BIND" && command.source.kind === "processParameter") consumed.add(command.alias.trim());
    const source: CompositionSource | undefined = "source" in command ? command.source : undefined;
    if (source?.kind === "componentValue") consumed.add(source.alias.trim());
  }
  return consumed;
}

/**
 * Refuse a control this script would place with nothing for the user to choose from.
 *
 * A control whose value is PICKED keeps the choices in a property of its own, and a placement that
 * never fills it ships something that opens empty and can never be used — the table beside it at
 * least says "no data". Live `eacfcdb9`: two task screens each got a category filter, wired
 * correctly to its own `onChange` read, and neither control had one option; the application
 * declared no category set anywhere, so there was nothing the author could have written. Refusing
 * the placement is what makes that visible: the lines hung on the alias fall with it (they say so
 * themselves), the list keeps the read it already had, and the requirement is reported unmet
 * instead of shipped dead.
 *
 * Returns the refusal text, or undefined when the component needs no list or this step gives it one.
 */
function refuseUnfilledChoiceControl(
  scoped: IScreenDesignContextState,
  componentName: string,
  alias: string,
  sourced: Map<string, Set<string>>,
  consumed: ReadonlySet<string>
): string | undefined {
  const catalogComponent: IComponent | undefined = findCatalogComponent(scoped.project, componentName);
  const choiceProperties: string[] = pickedValueListPropertiesOf(catalogComponent);
  if (choiceProperties.length === 0) return undefined;

  // Second half of the same question. A control needs a list AND a reader; with either missing it
  // is something the user can look at and not use, which is worse than a screen that never
  // pretended to offer it.
  if (!consumed.has(alias.trim())) {
    return (
      `${componentName} would be placed as ${alias} and nothing on this step reads it, so it is a control the ` +
      `user can change with no effect. Give it a reader — a read that runs when it changes ` +
      `(\`ACTION ${alias} onChange "<ActionName>" AS <#read>\`) or an argument that takes its value ` +
      `(\`ARG <#read> "<parameterName>" component:${alias}.value\`) — or do not place it.`
    );
  }

  const given: Set<string> = sourced.get(alias.trim()) ?? new Set<string>();
  const unfilled: boolean = choiceProperties.every(
    (property: string): boolean => !given.has(property.trim().toLowerCase())
  );
  if (!unfilled) return undefined;
  const first: string = choiceProperties[0];
  return (
    `${componentName} shows a list the user picks from and this step never gives ${alias} one, so it ` +
    `would open empty. Its list has two sources: an action — \`DATA ${alias} ${first} ` +
    `"<ActionName>"\` (+ \`DATAFIELD ${alias} ${first} fieldToUseAsKey|fieldToDisplay <Column>\` to ` +
    `name the columns) — or a literal set, \`DATA ${alias} ${first} fixed\` plus one ` +
    `\`OPTION ${alias} ${first} "<value>" ["<label>"]\` per choice (your brief's \`enumFields\` ` +
    `carries the app-wide set for a column that has one). If this application declares neither, do ` +
    `not place the control: leave the list on the read it already has and say the filter could not ` +
    `be built.`
  );
}

/**
 * Run one step's composition lines against its screen, then send them.
 *
 * Lines fail one at a time: a refused placement does not stop the next line, it only leaves its
 * alias unresolvable — which every line that names it then reports for itself. What DOES stop the
 * step is a failure to reach the screen at all, because then no line has a target.
 */
async function composeStep(
  sdInstance: ScreenDesignInstance,
  workspace: IStepWorkspace,
  commands: readonly ScreenCompositionCommand[]
): Promise<IScreenCompositionLineResult[]> {
  const results: IScreenCompositionLineResult[] = [];
  const screenId: string = workspace.screenId;
  const sourced: Map<string, Set<string>> = sourcedPropertiesByAlias(commands);
  const consumed: ReadonlySet<string> = consumedAliases(commands);

  const target = (): IScreenDesignContextState | undefined => tryResolveEditTarget(sdInstance, screenId);

  // KOA-18431 — a line runs only once the line that DECLARES the alias it reads has run. The
  // grammar has always promised "line order does not matter" and the validator and the step router
  // both honour it; this is the third layer agreeing. See `orderByDeclaration` for why lines are
  // deferred rather than sorted.
  for (const command of orderByDeclaration(commands)) {
    const scoped: IScreenDesignContextState | undefined = target();
    if (scoped === undefined) {
      results.push(fail(command, `Screen "${screenId}" could not be read, so this line was not applied.`));
      continue;
    }

    if (command.kind === "PLACE") {
      const anchor: number | string = resolveAnchor(command.anchor, workspace);
      if (typeof anchor === "string") {
        results.push(fail(command, anchor));
        continue;
      }
      const unfilled: string | undefined = refuseUnfilledChoiceControl(
        scoped,
        command.componentName,
        command.alias,
        sourced,
        consumed
      );
      if (unfilled !== undefined) {
        results.push(fail(command, unfilled));
        continue;
      }
      const placed = placeComponent(sdInstance, {
        screenId,
        anchorKuikaId: anchor,
        componentName: command.componentName
      });
      if (isPlacementRefusal(placed)) {
        results.push(fail(command, placed.error));
        continue;
      }
      const result: IPlacementResult = placed;
      workspace.components.set(command.alias, result.kuikaId);
      workspace.createdComponents.set(result.kuikaId, command.componentName);
      workspace.operations = stageInsert(workspace.operations, result, scoped.screenDesignDictionary);
      results.push(ok(command, `${command.componentName} placed as ${command.alias} (${result.kuikaId}).`));
      continue;
    }

    if (command.kind === "CHILD") {
      const host: number | undefined = workspace.components.get(command.hostAlias);
      if (host === undefined) {
        results.push(fail(command, unresolvedAlias(command.hostAlias, workspace, "component")));
        continue;
      }
      const unfilledChild: string | undefined = refuseUnfilledChoiceControl(
        scoped,
        command.componentName,
        command.alias,
        sourced,
        consumed
      );
      if (unfilledChild !== undefined) {
        results.push(fail(command, unfilledChild));
        continue;
      }
      const added = placeChildItem(sdInstance, {
        screenId,
        hostKuikaId: host,
        componentName: command.componentName,
        placeFirst: command.placeFirst
      });
      if (isPlacementRefusal(added)) {
        results.push(fail(command, added.error));
        continue;
      }
      const result: IPlacementResult = added;
      workspace.components.set(command.alias, result.kuikaId);
      workspace.createdComponents.set(result.kuikaId, command.componentName);
      workspace.operations = stageInsert(workspace.operations, result, scoped.screenDesignDictionary);
      results.push(
        ok(command, `${command.componentName} added to ${command.hostAlias} as ${command.alias} (${result.kuikaId}).`)
      );
      continue;
    }

    if (command.kind === "FIND") {
      // A step ref means that step's screen BODY, exactly as it does on `ACTION` — and it is the
      // only host a script can name for something it did not place itself.
      const hostLabel: string = command.host.kind === "step" ? command.host.stepRef : command.host.alias;
      let host: number;
      if (command.host.kind === "step") {
        host = SpecialDIIds.rootDI;
      } else {
        const named: number | undefined = workspace.components.get(command.host.alias);
        if (named === undefined) {
          results.push(fail(command, unresolvedAlias(command.host.alias, workspace, "component")));
          continue;
        }
        host = named;
      }
      const inside: IDisplayItem[] = descendantsOf(scoped.screenDesignDictionary?.[host]);
      const matches: IDisplayItem[] = inside.filter(
        (child: IDisplayItem): boolean => child.item?.name === command.componentName
      );
      const found: IDisplayItem | undefined = matches[command.ordinal - 1];
      if (found === undefined) {
        // Name what IS there. A script that guessed the wrong name has no other way to learn the
        // right one — it cannot see the screen, and the name it wants is usually a relative of the
        // one it wrote (`TableColumn` for `TableDefaultColumn`).
        const present: string = inside
          .map((child: IDisplayItem): string => String(child.item?.name ?? "?"))
          .filter((name: string, index: number, all: string[]): boolean => all.indexOf(name) === index)
          .join(", ");
        const what: string =
          matches.length === 0
            ? `holds no ${command.componentName}`
            : `holds only ${matches.length} ${command.componentName}, so there is no #${command.ordinal}`;
        results.push(
          fail(command, `${hostLabel} ${what}. It contains: ${present.length === 0 ? "(nothing)" : present}.`)
        );
        continue;
      }
      // NO `stageInsert`: this component was standing there before the line ran — either the
      // platform built it with its host, or an earlier script left it. Staging one would ask the
      // server to create a component it already holds.
      workspace.components.set(command.alias, found.id);
      results.push(ok(command, `${command.componentName} of ${hostLabel} is ${command.alias} (${found.id}).`));
      continue;
    }

    if (command.kind === "SET") {
      const kuikaId: number | undefined = workspace.components.get(command.alias);
      if (kuikaId === undefined) {
        results.push(fail(command, unresolvedAlias(command.alias, workspace, "component")));
        continue;
      }
      const written = setDesignerPropertyValue(sdInstance, {
        screenId,
        kuikaId: String(kuikaId),
        propertyName: command.propertyName,
        value: command.value
      });
      if ("error" in written && written.error !== undefined) {
        results.push(fail(command, written.error));
        continue;
      }
      workspace.operations = stageConfigure(
        workspace.operations,
        scoped.screenDesignDictionary,
        kuikaId,
        command.propertyName
      );
      results.push(ok(command, `${command.propertyName} of ${command.alias} set to ${String(command.value)}.`));
      continue;
    }

    if (command.kind === "BIND") {
      const kuikaId: number | undefined = workspace.components.get(command.alias);
      if (kuikaId === undefined) {
        results.push(fail(command, unresolvedAlias(command.alias, workspace, "component")));
        continue;
      }
      // Routed by WHICH writer the platform gives this property — see `writeBoundProperty`. A
      // datasource property, a row cell and an ordinary property are three different writes, and
      // performing all three as a symbol write is what shipped bound-looking, empty columns.
      const outcome: IWriteSuccess | IWriteRefusal = await writeBoundProperty({
        sdInstance,
        scoped,
        workspace,
        screenId,
        kuikaId,
        alias: command.alias,
        propertyName: command.propertyName,
        source: command.source
      });
      if (isWriteRefusal(outcome)) {
        results.push(fail(command, outcome.error));
        continue;
      }
      workspace.operations = stageConfigure(
        workspace.operations,
        scoped.screenDesignDictionary,
        kuikaId,
        command.propertyName
      );
      results.push(ok(command, outcome.message));
      continue;
    }

    if (command.kind === "DATA") {
      const kuikaId: number | undefined = workspace.components.get(command.alias);
      if (kuikaId === undefined) {
        results.push(fail(command, unresolvedAlias(command.alias, workspace, "component")));
        continue;
      }
      // `fixed` is the author's word for the platform's own literal option list; the platform
      // spells it "Fixed Option List" and the translation belongs here, not in the grammar.
      const value: string =
        command.actionName.trim().toLowerCase() === "fixed" ? FIXED_OPTION_LIST : command.actionName;
      const written = setDesignerPropertyDataAction(sdInstance, {
        screenId,
        kuikaId: String(kuikaId),
        propertyName: command.propertyName,
        value
      });
      if ("error" in written && written.error !== undefined) {
        results.push(fail(command, written.error));
        continue;
      }
      workspace.operations = stageConfigure(
        workspace.operations,
        scoped.screenDesignDictionary,
        kuikaId,
        command.propertyName
      );
      // A literal option list is a set of choices, not rows — only an ACTION-fed list has columns
      // to complete from.
      if (value !== FIXED_OPTION_LIST) {
        const read: IActionDefinitionDto | undefined = findActionDefinition(scoped, command.actionName);
        workspace.dataHosts.set(kuikaId, {
          kuikaId,
          propertyName: command.propertyName,
          filterFields: parameterNamesOf(read?.actionParameters)
        });
      }
      results.push(ok(command, `${command.propertyName} of ${command.alias} reads from ${value}.`));
      continue;
    }

    if (command.kind === "DATAFIELD") {
      const kuikaId: number | undefined = workspace.components.get(command.alias);
      if (kuikaId === undefined) {
        results.push(fail(command, unresolvedAlias(command.alias, workspace, "component")));
        continue;
      }
      // KOA-18431 — a FIXED list's own members are not the author's to name.
      //
      // Its entries are literally `{key, text}`, and the platform's writer sets
      // `fieldToUseAsKey`/`fieldToDisplay` to exactly those when it marks the symbol fixed. A
      // `DATAFIELD` naming a column instead overwrites them with names the list does not have, and
      // the generated dropdown then renders empty even with every entry present — the live run
      // wrote both lines against a fixed list (`c74f551e`). Column names belong to an ACTION-fed
      // list, where the platform derives them from that action's own result anyway.
      if (isOptionColumnKey(command.key) && isFixedOptionList(scoped, kuikaId, command.propertyName)) {
        results.push(
          fail(
            command,
            `${command.alias}'s ${command.propertyName} is a FIXED list, whose entries are value/label ` +
              `pairs — "${command.key}" names a column of an action's result and there is no action here. ` +
              `Write the choices with \`OPTION ${command.alias} ${command.propertyName} "<value>" ` +
              `["<label>"]\`, or point the property at an action with \`DATA ${command.alias} ` +
              `${command.propertyName} "<ActionName>"\` and let the platform read its columns.`
          )
        );
        continue;
      }
      const written = await setDesignerPropertyDataField(sdInstance, {
        screenId,
        kuikaId: String(kuikaId),
        propertyName: command.propertyName,
        key: command.key,
        value: command.value
      });
      if (written?.error !== undefined) {
        results.push(fail(command, written.error));
        continue;
      }
      workspace.operations = stageConfigure(
        workspace.operations,
        scoped.screenDesignDictionary,
        kuikaId,
        command.propertyName
      );
      results.push(
        ok(command, `${command.key} of ${command.alias}'s ${command.propertyName} set to ${command.value}.`)
      );
      continue;
    }

    if (command.kind === "OPTION") {
      const kuikaId: number | undefined = workspace.components.get(command.alias);
      if (kuikaId === undefined) {
        results.push(fail(command, unresolvedAlias(command.alias, workspace, "component")));
        continue;
      }
      // The list as it stands, read from the screen rather than accumulated here: a second script
      // adding a choice to a control an earlier one built must extend that list, not replace it.
      const existing: IFixedOption[] = readFixedOptions(scoped, kuikaId, command.propertyName);
      const wanted: string = command.value.trim().toLowerCase();
      const already: boolean = existing.some(
        (entry: IFixedOption): boolean => entry.key.trim().toLowerCase() === wanted
      );
      const next: IFixedOption[] = already
        ? existing.map((entry: IFixedOption): IFixedOption =>
            entry.key.trim().toLowerCase() === wanted ? { key: command.value, text: command.label } : entry
          )
        : [...existing, { key: command.value, text: command.label }];
      // `fixedValue` is the platform's own key for a literal option list, and its writer is what
      // marks the symbol fixed and names which member of each entry is the value and which is the
      // label. Writing the array through anything else produces a list the dropdown cannot read.
      const written = await setDesignerPropertyDataField(sdInstance, {
        screenId,
        kuikaId: String(kuikaId),
        propertyName: command.propertyName,
        key: "fixedValue",
        value: next
      });
      if (written?.error !== undefined) {
        results.push(fail(command, written.error));
        continue;
      }
      workspace.operations = stageConfigure(
        workspace.operations,
        scoped.screenDesignDictionary,
        kuikaId,
        command.propertyName
      );
      results.push(
        ok(
          command,
          `${command.alias}'s ${command.propertyName} ${already ? "now offers" : "offers"} "${command.label}" ` +
            `(${String(next.length)} choice(s)).`
        )
      );
      continue;
    }

    if (command.kind === "ACTION") {
      // The host is a placed component, or the step's screen BODY — which is where `pageInit`
      // lives. `hostLabel` is what the author wrote, so a refusal names their own token.
      const hostLabel: string = command.host.kind === "step" ? command.host.stepRef : command.host.alias;
      let kuikaId: number;
      if (command.host.kind === "step") {
        kuikaId = SpecialDIIds.rootDI;
      } else {
        const placed: number | undefined = workspace.components.get(command.host.alias);
        if (placed === undefined) {
          results.push(fail(command, unresolvedAlias(command.host.alias, workspace, "component")));
          continue;
        }
        kuikaId = placed;
      }

      // KOA-18136 §58 — the engine's own process family is off a task screen's menu, and this is
      // the surface that can still name one. Refused before the lookup: the action EXISTS, which
      // is exactly why teaching alone would not hold.
      if (isEngineProcessAction(command.actionName)) {
        results.push(
          fail(
            command,
            `"${command.actionName}" is one of the engine's own process actions and may not be ` +
              `attached to a task screen. This screen already carries the step its assignee is ` +
              `holding; the engine drives the instance, and its admin reads answer for every user. ` +
              `Attach a datasource action of this application instead, or leave the control off.`
          )
        );
        continue;
      }
      const definition: IActionDefinitionDto | undefined = findActionDefinition(scoped, command.actionName);
      if (definition === undefined) {
        results.push(fail(command, `This project has no action named "${command.actionName}".`));
        continue;
      }
      // KOA-18431 — THE EVENT MUST BE ONE THE HOST DECLARES.
      //
      // The one input in this whole verb set nothing checked. Every other name is answered by the
      // platform: a component by the catalog, a property by the property writer, an action by the
      // project, a parameter by the action. An event name was written through verbatim — so live
      // `02cda57d` put `pageInit` on a Table, which declares six events and none of them that one.
      // The action persisted, the property panel had no row to show it in (it renders the
      // DECLARED events), and nothing fires it at runtime. Green line, dead screen.
      const declared: readonly string[] | undefined = declaredEventNames(scoped, kuikaId);
      const known: boolean =
        declared === undefined ||
        declared.some((candidate: string): boolean => candidate.toLowerCase() === command.eventName.toLowerCase());
      if (!known) {
        results.push(
          fail(
            command,
            `"${command.eventName}" is not an event of ${hostLabel}. It offers: ` +
              `${(declared ?? []).length > 0 ? (declared ?? []).join(", ") : "(none)"}. A screen-LOAD read belongs on ` +
              `the step's body — write \`ACTION <stepRef> pageInit "…"\` — not on the component it fills.`
          )
        );
        continue;
      }
      const action: IAction = buildActionInstance(definition);
      const event: IEvent = { eventName: command.eventName, actions: [] } as unknown as IEvent;
      const outcome = scoped.saveActionToEvent(String(kuikaId), action, event, ESdChangeReason.DiActionUpdated);
      if (outcome?.attachedToComponent !== true) {
        // Read back rather than assumed — the platform's own verdict, for the same reason the
        // attach handler stopped trusting the branch it took (KOA-18226).
        results.push(
          fail(command, `"${command.actionName}" did not land on ${hostLabel}; the write was routed elsewhere.`)
        );
        continue;
      }
      if (command.actionAlias !== undefined) {
        workspace.actions.set(command.actionAlias, { actionId: action.id, hostKuikaId: kuikaId });
      }
      workspace.operations = stageWire(workspace.operations, scoped.screenDesignDictionary, kuikaId);
      results.push(ok(command, `"${command.actionName}" attached to ${command.eventName} of ${hostLabel}.`));
      continue;
    }

    if (command.kind === "ARG") {
      const attached: IAttachedAction | undefined = workspace.actions.get(command.actionAlias);
      if (attached === undefined) {
        results.push(fail(command, unresolvedAlias(command.actionAlias, workspace, "action")));
        continue;
      }
      const found = findAttachedAction(scoped, attached);
      if (found === undefined) {
        results.push(fail(command, `${command.actionAlias} is no longer on this screen.`));
        continue;
      }
      const parameter: IActionParameter | undefined = (found.action.parameters ?? []).find(
        (candidate: IActionParameter): boolean =>
          (candidate.name ?? "").toLowerCase() === command.parameterName.toLowerCase()
      );
      if (parameter === undefined) {
        const names: string = (found.action.parameters ?? [])
          .map((candidate: IActionParameter): string => candidate.name)
          .join(", ");
        results.push(
          fail(command, `"${command.parameterName}" is not a parameter of that action. It takes: ${names || "(none)"}.`)
        );
        continue;
      }
      // KOA-18431 — AN INPUT MUST HAVE A VALUE WHEN THE ACTION RUNS.
      //
      // A read attached to the screen BODY runs while the screen is coming up, before the assignee
      // has touched anything, so every control on it still holds its empty starting value. Feeding
      // such a read from one of them binds it to nothing: live `c74f551e` gave a query that
      // REQUIRES a category the category from the SelectBox next to it, and the list was empty on
      // every open. Refused at the line, because it ships silently otherwise — the binding is
      // real, the query runs, and it simply never matches a row.
      if (command.source.kind === "componentValue" && isScreenLoadEvent(found.event.eventName ?? "")) {
        results.push(
          fail(
            command,
            `"${command.parameterName}" is fed from ${command.source.alias}, but ${command.actionAlias} runs on ` +
              `${found.event.eventName} — while the screen is still coming up, when that control is empty. The ` +
              `read would run once with no value. Either feed it from something the screen already carries ` +
              `(\`process:<parameter>\`, a literal), or attach this read to the control's own event ` +
              `(\`ACTION ${command.source.alias} onChange "<ActionName>" AS ${command.actionAlias}\`) so it runs ` +
              `when the value exists.`
          )
        );
        continue;
      }
      const input: IBuildDesignerSymbolInput | string = bindingInputFor(command.source, workspace, screenId);
      if (typeof input === "string") {
        results.push(fail(command, input));
        continue;
      }
      // The parameter's declared type is the bound-to side, so a source with a fixed runtime type
      // records the conversion the picker would have recorded.
      const built: ISymbol | { error: string } = buildDesignerSymbol(
        { ...input, targetType: parameter.Typename === undefined ? undefined : String(parameter.Typename) },
        sdInstance
      );
      if ("error" in built) {
        results.push(fail(command, built.error));
        continue;
      }
      // KOA-17717's enrichment, on the path that had been skipping it: a row-context symbol built
      // deterministically carries a hardcoded "String" type and no row reference, and code
      // generation resolves the row through `diId`. The platform's own ordinary-screen path calls
      // this at the same seam.
      const symbol: ISymbol = enrichCurrentRowSymbol(
        built as ISymbol,
        sdInstance,
        attached.hostKuikaId,
        parameter.Typename === undefined ? undefined : String(parameter.Typename),
        screenId
      );
      const nextAction: IAction = setDesignerActionParameter(found.action, parameter, symbol as ISymbol);
      const outcome = scoped.saveActionToEvent(
        String(attached.hostKuikaId),
        nextAction,
        found.event,
        ESdChangeReason.DiActionUpdated
      );
      if (outcome?.attachedToComponent !== true) {
        results.push(fail(command, `"${command.parameterName}" could not be written to ${command.actionAlias}.`));
        continue;
      }
      workspace.operations = stageWire(workspace.operations, scoped.screenDesignDictionary, attached.hostKuikaId);
      results.push(ok(command, `${command.parameterName} of ${command.actionAlias} bound.`));
      continue;
    }

    if (command.kind === "ORDER") {
      const moved: number | undefined = workspace.components.get(command.alias);
      const before: number | undefined = workspace.components.get(command.beforeAlias);
      if (moved === undefined || before === undefined) {
        const missing: string = moved === undefined ? command.alias : command.beforeAlias;
        results.push(fail(command, unresolvedAlias(missing, workspace, "component")));
        continue;
      }
      const reordered = reorderSibling(sdInstance, { screenId, kuikaId: moved, beforeKuikaId: before });
      if (isPlacementRefusal(reordered)) {
        results.push(fail(command, reordered.error));
        continue;
      }
      const result: IReorderResult = reordered;
      workspace.operations = stageReorder(workspace.operations, scoped.screenDesignDictionary, result.parentKuikaId);
      results.push(ok(command, `${command.alias} moved in front of ${command.beforeAlias}.`));
      continue;
    }

    // DROP — the alias says WHAT is being removed.
    //
    // `#alias` is one namespace covering components and attached actions, so `DROP #getir` on an
    // action removes the action and `DROP #liste` on a component removes the component. Asking the
    // workspace which table holds the name is the whole decision; a separate verb would make the
    // author state something the alias already says.
    if (command.target.kind === "alias") {
      const attachedAction: IAttachedAction | undefined = workspace.actions.get(command.target.alias);
      if (attachedAction !== undefined) {
        const found = findAttachedAction(scoped, attachedAction);
        if (found === undefined) {
          results.push(fail(command, `${command.target.alias} is no longer on this screen.`));
          continue;
        }
        try {
          scoped.deleteActionFromEvent(String(attachedAction.hostKuikaId), found.action, found.event);
        } catch (error: unknown) {
          // A NAVIGATE action owns a hidden navigation component, and removing THAT is a canvas
          // operation a detached session refuses by design. Said plainly rather than swallowed:
          // a half-removed action is worse than a refusal the author can act on.
          const reason: string = error instanceof Error ? error.message : String(error);
          results.push(
            fail(
              command,
              `${command.target.alias} could not be removed: ${reason} A navigation action also owns a ` +
                `hidden navigation component, which can only be removed on the screen the designer has open.`
            )
          );
          continue;
        }
        workspace.actions.delete(command.target.alias);
        workspace.operations = stageWire(
          workspace.operations,
          scoped.screenDesignDictionary,
          attachedAction.hostKuikaId
        );
        results.push(ok(command, `${command.target.alias} removed from its event.`));
        continue;
      }
    }

    const dropTarget: number | string = resolveAnchor(command.target, workspace);
    if (typeof dropTarget === "string") {
      results.push(fail(command, dropTarget));
      continue;
    }
    const removed = removeComponent(sdInstance, { screenId, kuikaId: dropTarget });
    if (isPlacementRefusal(removed)) {
      results.push(fail(command, removed.error));
      continue;
    }
    const removal: IRemovalResult = removed;
    workspace.operations = recordDelete(workspace.operations, {
      kuikaId: removal.kuikaId,
      removedKuikaIds: removal.removedKuikaIds
    });
    // An alias whose component left the screen must stop resolving, or a later line would address
    // an id the tree no longer has.
    for (const [alias, id] of [...workspace.components.entries()]) {
      if (removal.removedKuikaIds.includes(id)) workspace.components.delete(alias);
    }
    results.push(ok(command, `Component ${dropTarget} removed.`));
  }

  return results;
}

/**
 * A column of a list, by the catalog's own naming.
 *
 * Every repeater's column type ends in `Column` — `TableDefaultColumn`, `TableTemplateColumn`,
 * `ReportTableColumn` — and a new one gains the same suffix, so this answers for it unchanged
 * rather than from a list that would have to be maintained.
 */
function isColumnComponent(componentName: string): boolean {
  return componentName.trim().toLowerCase().endsWith("column");
}

/** Whether a symbol carries ANY value — a field, a literal, an input, a default, a component. */
function symbolCarriesValue(symbol: ISymbol | undefined): boolean {
  if (symbol === undefined) return false;
  const nonEmpty = (value: unknown): boolean => typeof value === "string" && value.trim().length > 0;
  if (nonEmpty(symbol.fieldName)) return true;
  if (nonEmpty(symbol.fixedValue)) return true;
  if (nonEmpty(symbol.inputName)) return true;
  if (nonEmpty(symbol.defaultValueName)) return true;
  if (nonEmpty(symbol.bagName)) return true;
  if (nonEmpty(symbol.actionDefinitionId)) return true;
  return typeof symbol.diId === "number";
}

/**
 * Whether a column would show a reader ANYTHING — KOA-18431.
 *
 * Its header is not an answer: a column headed "Talep Tarihi" over a Label bound to nothing is
 * the shape live `c74f551e` shipped three of, and a header is what makes it look finished. So the
 * question is asked of everything BELOW the column (its cells) plus the column's own field, and
 * anything at all counts — a row field, a literal, a process value, a button that acts on the row.
 */
function columnShowsSomething(target: IScreenDesignContextState, columnKuikaId: number): boolean {
  const column: IDisplayItem | undefined = target.screenDesignDictionary?.[columnKuikaId];
  if (column === undefined) return true;
  // A default column carries the field it shows on ITSELF, next to the title.
  const ownField = (column.item?.properties ?? []).find(
    (property: IDisplayItemProperty): boolean => property.name === "defaultDataField"
  );
  if (symbolCarriesValue(ownField?.symbol)) return true;

  for (const descendant of descendantsOf(column)) {
    for (const property of descendant?.item?.properties ?? []) {
      if (symbolCarriesValue(property?.symbol)) return true;
    }
    for (const event of descendant?.item?.events ?? []) {
      if ((event.actions ?? []).length > 0) return true;
    }
  }
  return false;
}

/**
 * Take back a column this script created that shows nothing — KOA-18431.
 *
 * Live `c74f551e`: the author wrote three columns and the `BIND` that would have filled each cell
 * was refused, so the list shipped with three headers over empty cells and a green line for every
 * one of them. A refusal per line is the right report and the wrong RESULT — what is left on the
 * screen is worse than what the author wrote, because a header promises a value.
 *
 * Only this script's own columns, only when nothing anywhere inside them carries a value, and
 * said on its own line: a removal nobody is told about is how a script and a screen drift apart.
 * Runs before the completion pass, so a field a removed column was meant to show can still get
 * one from the read.
 */
function sweepEmptyAuthoredColumns(
  sdInstance: ScreenDesignInstance,
  workspace: IStepWorkspace,
  lastLine: number
): IScreenCompositionLineResult[] {
  const results: IScreenCompositionLineResult[] = [];
  const removedNames: string[] = [];
  for (const [kuikaId, componentName] of [...workspace.createdComponents.entries()]) {
    if (!isColumnComponent(componentName)) continue;
    const scoped: IScreenDesignContextState | undefined = tryResolveEditTarget(sdInstance, workspace.screenId);
    if (scoped === undefined) break;
    if (columnShowsSomething(scoped, kuikaId)) continue;

    // Read before the tree changes — `settleRemoval` answers by walking the parent chain.
    const dictionaryBefore: IScreenDesignDictionary | undefined = scoped.screenDesignDictionary;
    const removed = removeComponent(sdInstance, { screenId: workspace.screenId, kuikaId });
    if (isPlacementRefusal(removed)) continue;
    const removal: IRemovalResult = removed;
    settleRemoval(workspace, dictionaryBefore, removal);
    for (const id of removal.removedKuikaIds) workspace.createdComponents.delete(id);
    for (const [alias, id] of [...workspace.components.entries()]) {
      if (removal.removedKuikaIds.includes(id)) workspace.components.delete(alias);
    }
    removedNames.push(String(kuikaId));
  }

  if (removedNames.length > 0) {
    results.push({
      line: lastLine,
      command: "COLUMNS",
      success: true,
      message:
        `${String(removedNames.length)} column(s) this script created showed nothing — no row field, no value, ` +
        `no action — and were taken back off the list (${removedNames.join(", ")}). A column with a header and ` +
        `empty cells reads as a value that failed to load. Bind the cell (\`FIND\` the Label inside the column, ` +
        `then \`BIND <#cell> value row:<Field>\`) and the column stays.`
    });
  }
  return results;
}

/**
 * What the LEDGER owes after this pass takes something back off the screen — KOA-18431.
 *
 * Three cases, and only the middle one is not `recordDelete`'s already:
 *
 *   - it has an insert op of its own → `recordDelete` drops that op and emits nothing;
 *   - it is INSIDE a subtree this script is already sending → nothing is owed at all. The insert
 *     is serialised from the tree at send time, so the removal is expressed by its absence, and a
 *     delete would name an id the server has never been told about (it comes back as a dropped
 *     change);
 *   - it was on the screen before this script ran → `recordDelete` emits the delete.
 *
 * Asked with the dictionary as it was BEFORE the removal, because the middle case is answered by
 * walking the parent chain.
 */
function settleRemoval(
  workspace: IStepWorkspace,
  dictionaryBefore: IScreenDesignDictionary | undefined,
  removal: IRemovalResult
): void {
  const hasOwnInsert: boolean = isLocallyInserted(workspace.operations, removal.kuikaId);
  const insideAnother: boolean =
    !hasOwnInsert && withinStagedInsert(workspace.operations, dictionaryBefore, removal.kuikaId);
  if (insideAnother) return;
  workspace.operations = recordDelete(workspace.operations, {
    kuikaId: removal.kuikaId,
    removedKuikaIds: removal.removedKuikaIds
  });
}

/** Take a column this pass just created back off the screen, ledger included. */
function discardColumn(sdInstance: ScreenDesignInstance, workspace: IStepWorkspace, columnKuikaId: number): void {
  const scoped: IScreenDesignContextState | undefined = tryResolveEditTarget(sdInstance, workspace.screenId);
  const dictionaryBefore: IScreenDesignDictionary | undefined = scoped?.screenDesignDictionary;
  const removed = removeComponent(sdInstance, { screenId: workspace.screenId, kuikaId: columnKuikaId });
  if (isPlacementRefusal(removed)) return;
  settleRemoval(workspace, dictionaryBefore, removed);
}

/**
 * Give every list this script sourced a column for each field it can show — KOA-18431.
 *
 * Runs after the step's lines, because the columns an author wrote arrive across many of them and
 * the source may be bound on the last. What they wrote is untouched: their order, their titles, the
 * fields they chose. Only the fields the read returns and NOTHING on the list shows get a column,
 * appended after theirs.
 *
 * Per host, a failure is that host's alone — a list whose source cannot be resolved keeps what the
 * author gave it rather than costing the step.
 */
async function completeDataHostColumns(
  sdInstance: ScreenDesignInstance,
  workspace: IStepWorkspace,
  lastLine: number
): Promise<IScreenCompositionLineResult[]> {
  const results: IScreenCompositionLineResult[] = [];
  for (const host of workspace.dataHosts.values()) {
    const scoped: IScreenDesignContextState | undefined = tryResolveEditTarget(sdInstance, workspace.screenId);
    if (scoped === undefined) continue;

    const target = { screenId: workspace.screenId, kuikaId: String(host.kuikaId), propertyName: host.propertyName };
    const fields: IKTypeField[] | { error: string } = listDataHostFields(sdInstance, target);
    if (!Array.isArray(fields) || fields.length === 0) continue;

    const shown: ReadonlySet<string> = boundRowFields(scoped, host.kuikaId);
    const filteredBy: ReadonlySet<string> = new Set<string>(
      host.filterFields.map((name: string): string => foldFieldName(name))
    );
    const missing: IKTypeField[] = fields.filter(
      (field: IKTypeField): boolean =>
        isDisplayableRowField(field) &&
        !shown.has(rowFieldPath(field).trim().toLowerCase()) &&
        !filteredBy.has(foldFieldName(field.name ?? ""))
    );
    if (missing.length === 0) continue;

    const added: string[] = [];
    for (const field of missing) {
      // The same three writes the author would have made, through the same functions: the column
      // is a declared child of the table (the catalog puts it on the row), the value hangs on a
      // Label inside it, and the binding goes through the platform's column field picker.
      const column = placeChildItem(sdInstance, {
        screenId: workspace.screenId,
        hostKuikaId: host.kuikaId,
        componentName: "TableTemplateColumn"
      });
      if (isPlacementRefusal(column)) break;
      workspace.operations = stageInsert(workspace.operations, column, scoped.screenDesignDictionary);

      setDesignerPropertyValue(sdInstance, {
        screenId: workspace.screenId,
        kuikaId: String(column.kuikaId),
        propertyName: "title",
        value: humanizeFieldName(field.name)
      });

      const cell = placeComponent(sdInstance, {
        screenId: workspace.screenId,
        anchorKuikaId: column.kuikaId,
        componentName: "Label"
      });
      if (isPlacementRefusal(cell)) {
        discardColumn(sdInstance, workspace, column.kuikaId);
        continue;
      }

      const bound = await setDesignerPropertyDataField(sdInstance, {
        screenId: workspace.screenId,
        kuikaId: String(cell.kuikaId),
        propertyName: "value",
        key: "tableFieldBinding",
        value: JSON.stringify(field)
      });
      if (bound?.error !== undefined) {
        // This pass added the column a moment ago and could not fill it. Leaving it would put the
        // very shape the sweep above exists to remove back on the screen, under a header this
        // pass invented — worse than the silence of not adding it at all.
        discardColumn(sdInstance, workspace, column.kuikaId);
        continue;
      }
      added.push(rowFieldPath(field));
    }

    if (added.length > 0) {
      results.push({
        line: lastLine,
        command: "COLUMNS",
        success: true,
        message:
          `The list also shows ${added.join(", ")} — ${String(added.length)} field(s) its read returns that ` +
          `no column was written for. Write the column yourself to control its header and position.`
      });
    }
  }
  return results;
}

/** How the send went, said on its own line so a failed send is never hidden behind green lines. */
function describeSend(
  result: IApplyScreenCompositionResult,
  stepId: string,
  line: number
): IScreenCompositionLineResult {
  if (!result.isSuccess) {
    return {
      line,
      command: "APPLY",
      success: false,
      message: `The composition of step ${stepId} was NOT saved: ${result.message ?? "the server refused it."}`
    };
  }
  const dropped: string =
    result.droppedCount > 0 ? ` ${result.droppedCount} change(s) were dropped — their target had left the screen.` : "";
  return {
    line,
    command: "APPLY",
    success: true,
    message: `Saved ${result.sentCount} change(s) to step ${stepId}'s screen.${dropped}`
  };
}

/**
 * Compose and send every step's screen.
 *
 * Runs after the main loop, because `ASSIGN_SCREEN` is what brings the screens into existence.
 * Each step is independent: one step's missing screen refuses only its own lines.
 */
export async function runScreenCompositionPass(
  sdInstance: ScreenDesignInstance,
  commands: readonly ScreenCompositionCommand[],
  resolveStepRef: StepRefResolver
): Promise<IScreenCompositionLineResult[]> {
  if (commands.length === 0) return [];

  const results: IScreenCompositionLineResult[] = [];
  const stepByAlias: Map<string, string> = mapAliasesToSteps(commands);

  // Group by the step ref as written, preserving first-seen order so the report reads like the
  // script. Refs are resolved per group, once.
  const byStepRef: Map<string, ScreenCompositionCommand[]> = new Map<string, ScreenCompositionCommand[]>();
  for (const command of commands) {
    const stepRef: string | undefined = stepOfCommand(command, stepByAlias);
    if (stepRef === undefined) {
      results.push(
        fail(
          command,
          `This line addresses a component no PLACE in this script created, so there is no screen to ` +
            `apply it to. Name the component with \`PLACE <stepRef> <Component> AS <alias> IN <anchor>\` first.`
        )
      );
      continue;
    }
    const bucket: ScreenCompositionCommand[] | undefined = byStepRef.get(stepRef);
    if (bucket === undefined) byStepRef.set(stepRef, [command]);
    else bucket.push(command);
  }

  for (const [stepRef, stepCommands] of byStepRef) {
    // Re-read per step rather than once: `ASSIGN_SCREEN` created these links moments ago and
    // wrote them through the project provider, and a snapshot taken before that is exactly the
    // stale read that once made a freshly created screen look absent.
    const live: IScreenDesignContextState = getValues(sdInstance);

    let stepId: string;
    try {
      stepId = resolveStepRef(stepRef);
    } catch (error: unknown) {
      const message: string = error instanceof Error ? error.message : String(error);
      for (const command of stepCommands) results.push(fail(command, message));
      continue;
    }

    // The registry answers both halves of the same question, so it is asked once: which workflow
    // this step belongs to, and which screen it owns. A step with no link yields neither.
    const processId: string = findProcessIdForStep(live, stepId);
    const screenId: string | null = findManagedScreenIdForStep(live.project, processId, stepId);
    if (screenId === null) {
      for (const command of stepCommands) {
        results.push(
          fail(
            command,
            `Step ${stepId} has no platform-managed screen to compose on. Put \`ASSIGN_SCREEN <workflowId> ` +
              `${stepRef}\` in this SAME script — it is what generates the screen these lines build on.`
          )
        );
      }
      continue;
    }

    // The open canvas IS the right tree when this screen happens to be the one on it; a second
    // copy would diverge from what the user can see.
    const isOpenScreen: boolean = live.sdId === screenId;
    let ownsSession: boolean = false;
    if (!isOpenScreen) {
      const session = await openDetachedScreenSession(sdInstance, screenId, true);
      if (session === undefined) {
        for (const command of stepCommands) {
          results.push(fail(command, `Screen "${screenId}" could not be read, so step ${stepId} was not composed.`));
        }
        continue;
      }
      ownsSession = true;
    }

    const workspace: IStepWorkspace = {
      stepId,
      screenId,
      ownsSession,
      components: new Map<string, number>(),
      actions: new Map<string, IAttachedAction>(),
      operations: [],
      dataHosts: new Map<number, IDataHostBinding>(),
      createdComponents: new Map<number, string>(),
      declaredAliases: new Set<string>(
        stepCommands
          .map(compositionDeclaredAlias)
          .filter((alias: string | undefined): alias is string => alias !== undefined)
      )
    };

    try {
      const stepResults: IScreenCompositionLineResult[] = await composeStep(sdInstance, workspace, stepCommands);
      // Deferral changes the order lines RUN in, never the order they are reported in.
      results.push(...[...stepResults].sort((a, b): number => a.line - b.line));

      // KOA-18431 — a list shows what its READ returns. After the author's lines, because the
      // columns they wrote arrive across many of them and the source may be bound on the last.
      const lastLine: number = stepCommands[stepCommands.length - 1].line;
      results.push(...sweepEmptyAuthoredColumns(sdInstance, workspace, lastLine));
      results.push(...(await completeDataHostColumns(sdInstance, workspace, lastLine)));

      if (workspace.operations.length > 0) {
        const sent: IApplyScreenCompositionResult = await applyScreenComposition(sdInstance, {
          processId,
          stepId,
          operations: workspace.operations
        });
        results.push(describeSend(sent, stepId, stepCommands[stepCommands.length - 1].line));
      }
    } finally {
      // The apply rewrote this screen server side, so the copy held here is stale the moment it
      // lands — dropped rather than patched, exactly as the screen builder did.
      if (workspace.ownsSession) closeDetachedScreenSession(screenId);
    }
  }

  return results;
}

/** Minimal shape of the project link this pass reads a step's process id from. */
interface IProcessStepLinkLike {
  processId?: unknown;
  stepId?: unknown;
}

/**
 * The workflow a step belongs to, read from the project's own step→screen registry.
 *
 * The registry is what named the screen a moment ago, so it is also what names the process — and
 * asking it keeps this pass independent of whichever `ASSIGN_SCREEN` line happened to create the
 * link.
 */
function findProcessIdForStep(live: IScreenDesignContextState, stepId: string): string {
  const links: IProcessStepLinkLike[] = (live.project?.processStepScreenIds ?? []) as IProcessStepLinkLike[];
  for (const link of links) {
    if (String(link?.stepId ?? "") === stepId) return String(link?.processId ?? "");
  }
  return "";
}
